import { yupResolver } from '@hookform/resolvers/yup';
import { AxiosError, AxiosResponse } from 'axios';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { queryKeyWithLanguage } from 'utils/language';
import { setToken } from 'utils/local-storage-token';

import { getUserData, passwordUpdate, confirmRegistration } from 'services/registration';

import { QUERY_KEYS } from 'constants/query-keys';
import { PATHS } from 'constants/routes';

import { useUser } from 'hooks/useUser';

import {
    IPasswordUpdateFormValues,
    IRegistrationFormValues,
} from './index';
import { schema, schemaChange } from './validation';

import { APIErrors } from 'types/api';
import { PasswordUpdateDTO, SignInDTO } from 'types/user';

const defaultValues = {
    login: '',
    password: ''
};

const defaultValuesChange = {
    new_password: '',
    new_password_repeat: '',
};

export const useRegistrationEmployee = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [showPassword, isShowPassword] = useState<boolean>(false);
    const { setUserData, isAuthenticated } = useUser();
    const [showNewPassword, isShowNewPassword] = useState<boolean>(false);
    const [showPasswordSecond, isShowPasswordSecond] = useState<boolean>(false);
    const [loginPassword, setLoginPassword] = useState("");
    
    const {
        register: registerChange,
        handleSubmit: handleSubmitChange,
        formState: { errors: changeErrors },
        setError: setErrorChange
    } = useForm<IPasswordUpdateFormValues>({
        resolver: yupResolver(schemaChange),
        defaultValues: defaultValuesChange,
    });

    const onSubmitResetPassword = (formObject) => {
        if (!loginPassword) {
            return toast.error(t('passwordUpdate.somethingWentWrong'));
        }

        const prepareObject = formObject;
        prepareObject['password'] = loginPassword;
        passwordUpdateMutation.mutate(formObject);
    };

    const passwordUpdateMutation = useMutation<
        AxiosResponse<{ message: string }>,
        AxiosError<APIErrors>,
        PasswordUpdateDTO
    >((data) => passwordUpdate(data), {
        onSuccess: (data) => {
            toast.success(data?.data?.message);
            navigate(PATHS.HOME);
        },
        onError: (error) => {
            // set errors from backend request
            const formErrors = error?.response?.data?.errors;
            if (formErrors) {
                Object.entries(formErrors).map(([key, value]) => {
                    setErrorChange(key as keyof IPasswordUpdateFormValues, {
                        message: value?.[0],
                    });
                });
            }
        },
    });

    const { refetch: refetchUserData } = useQuery<AxiosResponse, AxiosError>(
        queryKeyWithLanguage(QUERY_KEYS.GET_USER_DATA),
        {
            queryFn: () => getUserData(),
            enabled: false,
            onSuccess: (res) => {
                setUserData(res?.data);
            },
        }
    );

    const signInMutation = useMutation<
        AxiosResponse<{ token: string }>,
        AxiosError<APIErrors>,
        SignInDTO
    >((data) => confirmRegistration(data), {
        onSuccess: async (data) => {
            setToken(data.data.token);
            await refetchUserData();
        },
    });

    const showPasswordHandle = () => {
        isShowPassword(!showPassword)
    };

    const {
        register,
        handleSubmit,
        formState: { errors: loginErrors },
        setError,
    } = useForm<IRegistrationFormValues>({
        resolver: yupResolver(schema),
        defaultValues: defaultValues,
    });

    const showNewPasswordHandle = () => {
        isShowNewPassword(!showNewPassword)
    };

    const showPasswordHandleSecond = () => {
        isShowPasswordSecond(!showPasswordSecond)
    };

    const onSubmitLogin = (formObject) => {
        setLoginPassword(formObject['password']);
        signInMutation.mutate(formObject);
    };

    return {
        isAuthenticated,
        handleSubmitChange,
        onSubmitResetPassword,
        showNewPasswordHandle,
        showPasswordHandleSecond,
        onSubmitLogin,
        register,
        handleSubmit,
        showPasswordHandle,
        registerChange,
        changeErrors,
        showPassword,
        showPasswordSecond,
        t,
        loginErrors,
        showNewPassword
    };
};
